import { useCallback } from 'react';

/**
 * Custom hook that provides dynamic item height calculation for list and grid layouts.
 * Supports virtual scrolling, custom height functions, and predefined type-based heights.
 *
 * @param {boolean} virtual - Whether virtual scrolling is enabled
 * @param {Function} getVirtualItemHeight - Function to get virtual item height by index
 * @param {number|string|Function} itemHeight - Static height, 'auto', or function returning height
 * @param {Array} data - Array of data items
 * @param {string} type - Item type for default height lookup (e.g., 'task-list', 'application-list')
 * @returns {{getItemHeight: Function}} Object containing height calculation function
 */
export default function useItemHeight(virtual, getVirtualItemHeight, itemHeight, data, type) {
  /**
   * Calculates the height of an item at a specific index
   * @param {number} index - The index of the item in the data array
   * @returns {number} The calculated height in pixels
   */
  const getItemHeight = useCallback(
    (index) => {
      if (virtual) return getVirtualItemHeight(index);
      if (itemHeight !== 'auto') {
        return typeof itemHeight === 'function' ? itemHeight(index, data[index]) : itemHeight;
      }

      // Default heights based on item type
      const defaultHeights = {
        'task-list': 180,
        'old-task-list': 180,
        'application-list': 120,
        'bulk-upload-list': 120,
        'square-item': 200,
        'process-item': 220,
        'ai-agent-item': 200,
        'rpa-agent-item': 200,
        'group-item': 100,
        'toggle-item': 140,
        'config-action-item': 100,
        'iteration-item': 120,
        'switchCase-item': 120,
        'sendEMail-item': 120,
      };

      return defaultHeights[type] || 150;
    },
    [virtual, getVirtualItemHeight, itemHeight, data, type],
  );

  return { getItemHeight };
}
